import React, { useState } from 'react';
import LineGraph from '../Graph/Graph';
import styles from './GraphCard.module.scss';
import dottedLineLight from '../../../assets/images/LightTheme/dottedLight.svg';
import planeLineDark from '../../../assets/images/DarkTheme/lineDark.svg';
import dottedLineDark from '../../../assets/images/DarkTheme/dottedDark.svg';
import planeLineLight from '../../../assets/images/LightTheme/lineLight.svg';
import { useAppSelector } from '../../../redux/hooks';
import { useTranslation } from 'react-i18next';
import { CurrencyInputFormatter } from '../../../utils/currencyFormatter';
import { NumberFormatter } from '../../../utils/NumberFormatter';
import { getPercentageParts } from '../../../utils/agcUtil';

interface CardProps {
  lineChartData: LineChartData;
  barChartData?: any;
  activationPeriod?: any;
  isActivartionRate?: boolean;
  currency: string;
  chartSummaryData?: ChartSummaryData;
  truncateDecimals?: boolean;
}

const GraphCard: React.FC<CardProps> = ({
  lineChartData,
  barChartData,
  activationPeriod,
  isActivartionRate,
  currency,
  chartSummaryData,
  truncateDecimals = false,
}) => {
  const currentTheme = useAppSelector((state: any) => state.theme.currentTheme);
  const dottedLine = currentTheme === 'light' ? dottedLineLight : dottedLineDark;
  const planeLine = currentTheme === 'light' ? planeLineLight : planeLineDark;
  const { t } = useTranslation();

  const currentValue = chartSummaryData?.kpiValue;
  const kpiValueDatatype = chartSummaryData?.kpiDataType;
  const kpiVarValue = chartSummaryData?.kpiVarianceValue;
  const kpiVarType = chartSummaryData?.kpiVarianceDataType;

  const currentYear = lineChartData?.body?.lines[0]?.lineName;
  const prevYear = lineChartData?.body?.lines[1]?.lineName;
  const percentageChange = kpiVarValue === undefined ? undefined : parseFloat(kpiVarValue);
  const absolutePercentageChange =
    percentageChange !== undefined ? Math.abs(percentageChange) : undefined;
  const userDetails = useAppSelector((state: any) => state.userDetails.user);
  const [userAffiliateCode] = useState(userDetails?.userAffiliateCode || 0);
  const pc = getPercentageParts(percentageChange);

  // Kpi value formatter added.
  const formattedPercentageChange =
    absolutePercentageChange !== undefined
      ? kpiVarType === 'percentage'
        ? `${truncateDecimals ? Math.round(absolutePercentageChange) : absolutePercentageChange}%`
        : kpiVarType === 'currency'
          ? `${CurrencyInputFormatter(currency, absolutePercentageChange, truncateDecimals)}`
          : `${absolutePercentageChange}`
      : 'N/A';

  const formattedValue =
    currentValue !== undefined
      ? kpiValueDatatype === 'percentage'
        ? `${currentValue}%`
        : kpiValueDatatype === 'currency'
          ? `${CurrencyInputFormatter(currency, currentValue, truncateDecimals)}`
          : kpiValueDatatype === 'number'
            ? `${NumberFormatter(userAffiliateCode, currentValue, truncateDecimals)}`
            : `${currentValue}`
      : 'N/A';

  // Year Month Formatter added.
  const closingMonth = chartSummaryData?.closingMonth;
  const comparisonMonth = chartSummaryData?.kpiVarianceComparisonMonth;
  const formatMonth = (monthString: string): string => {
    if (!monthString || monthString.length !== 6) return '';
    const year = monthString?.slice(0, 4);
    const month = parseInt(monthString?.slice(4, 6), 10) - 1;
    const date = new Date(Date.UTC(parseInt(year, 10), month, 1));
    const monthName = t(
      `chartmonths.${date?.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' })}`
    );
    return `${year} ${monthName}`;
  };
  const closingMonthFormatted = closingMonth ? formatMonth(closingMonth) : null;
  const comparisonMonthFormatted = comparisonMonth ? formatMonth(comparisonMonth) : null;

  if (!lineChartData?.body?.lines?.length) {
    return <div className={`${styles.graph_card_container}`}>{t('noDataAvailable')}</div>;
  }
  return (
    <div className={`${styles.graph_card_container}`}>
      {/* Graph Section */}
      <div className={`${styles.graph_wrapper}`}>
        <LineGraph
          lineChartData={lineChartData}
          barChartData={barChartData}
          activationPeriod={activationPeriod}
          isActivartionRate={isActivartionRate}
          currency={currency}
          affiliateCode={userAffiliateCode}
          truncateDecimals={truncateDecimals}
        />
      </div>

      {/* Footer Section */}
      <div className={`${styles.card_footer}`}>
        {/* Left Section - Month Data */}
        <div className={styles.month_data}>
          <div className={`${styles.current_month_year}`}>{closingMonthFormatted}</div>
          {currentValue !== null ? (
            <div className={`data_value ${styles.data_value}`}>{formattedValue}</div>
          ) : (
            <div>{t('noDataAvailable')}</div>
          )}
          {kpiVarValue !== null && (
            <div
              className={`${styles.value_info_wrapper} ${
                percentageChange !== undefined && percentageChange >= 0
                  ? styles.raised_change
                  : styles.fall_change
              }`}
            >
              <span className={`${styles.percentage_value_change}`}>
                {pc.arrow}
                {pc.arrow ? ' ' : ''}
                {percentageChange === undefined ? 'N/A' : formattedPercentageChange}
              </span>

              <span className={`${styles.value_diff}`}>
                {t('vs')} {comparisonMonthFormatted}
              </span>
            </div>
          )}
        </div>

        {/* Right Section - Legend */}
        <div className={styles.legend_wrapper}>
          <div className={styles.legend}>
            <div className={styles.line_type}>
              <img src={dottedLine} alt="line" />
            </div>
            <div>{prevYear}</div>
          </div>
          <div className={styles.legend}>
            <div className={styles.line_type}>
              <img src={planeLine} alt="line" />
            </div>
            <div>{currentYear}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GraphCard;
